import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import RouteMap from "@/components/RouteMap";
import ChatPanel from "@/components/ChatPanel";
import RatingModal from "@/components/RatingModal";
import LiveLocationSharer from "@/components/LiveLocationSharer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Bicycle, PersonSimpleRun, TennisBall, MapPin, Clock, Lightning, Users, CalendarBlank, SignOut, Star, Trash, Crown, Image as ImageIcon, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ProBadge from "@/components/ProBadge";

const sportMeta = {
  cycling: { Icon: Bicycle, color: "#007AFF", label: "CYCLING" },
  running: { Icon: PersonSimpleRun, color: "#FF3B30", label: "RUNNING" },
  padel:   { Icon: TennisBall, color: "#34C759", label: "PADEL" },
};

export default function ActivityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [liveLocations, setLiveLocations] = useState([]);
  const [showRating, setShowRating] = useState(false);
  const coverInputRef = useRef(null);

  const ratingPromptRef = useRef(false);
  const [searchParams] = (typeof window !== "undefined")
    ? [new URLSearchParams(window.location.search)]
    : [new URLSearchParams("")];
  const rateQuery = searchParams.get("rate") === "1";

  const load = useCallback(async () => {
    const { data } = await api.get(`/activities/${id}`);
    setA(data);
    const ids = [...(data.participants || []), ...(data.waitlist || [])];
    const profiles = await Promise.all(ids.map((uid) => api.get(`/users/${uid}`).then((r) => r.data).catch(() => null)));
    setParticipants(profiles.filter(Boolean));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const isPast = a?.starts_at ? new Date(a.starts_at).getTime() < Date.now() : false;
  const isParticipant = a?.participants?.includes(user?.user_id);
  const isHost = user?.user_id === a?.host_id;

  // Auto-open rating modal when activity is past, user is participant, and hasn't rated yet
  useEffect(() => {
    if (!a || !user) return;
    if (!isPast || !isParticipant) return;
    if (ratingPromptRef.current) return;
    if (!rateQuery && localStorage.getItem(`tf_rated_${a.activity_id}`) === "1") return;
    const others = (a.participants || []).filter((u) => u !== user.user_id);
    if (others.length === 0) return;
    ratingPromptRef.current = true;
    const tmr = setTimeout(() => setShowRating(true), rateQuery ? 200 : 600);
    return () => clearTimeout(tmr);
  }, [a, user, isPast, isParticipant, rateQuery]);

  if (loading || !a) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-6 py-20 label-overline">{t("common.loading")}</div>
      </div>
    );
  }

  const meta = sportMeta[a.sport] || sportMeta.cycling;
  const isWaitlisted = a.waitlist?.includes(user?.user_id);
  const isFull = a.participants.length >= a.max_participants;
  const startDate = new Date(a.starts_at);
  const canSeeChat = isParticipant || isHost;

  const join = async () => {
    try {
      const { data } = await api.post(`/activities/${id}/join`);
      if (data.status === "joined") toast.success(t("activity.joined"));
      else if (data.status === "waitlisted") toast(t("activity.waitlisted_toast"), { description: t("activity.waitlisted_desc") });
      else toast.message(data.status);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't join"); }
  };
  const leave = async () => {
    try {
      await api.post(`/activities/${id}/leave`);
      toast(t("activity.left"));
      await load();
    } catch (e) { toast.error("Couldn't leave"); }
  };
  const del = async () => {
    if (!window.confirm(t("activity.confirm_delete"))) return;
    try {
      await api.delete(`/activities/${id}`);
      toast.success("Deleted");
      nav("/dashboard");
    } catch (e) { toast.error("Couldn't delete"); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/upload/activity-cover/${a.activity_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("common.save"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const coverUrl = a.cover_url
    ? (a.cover_url.startsWith("http") ? a.cover_url : `${process.env.REACT_APP_BACKEND_URL}${a.cover_url}`)
    : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        {coverUrl && (
          <div className="relative -mx-6 sm:mx-0 h-56 sm:h-72 mb-8 overflow-hidden border-y sm:border border-[#262626]" data-testid="activity-cover-hero">
            <img src={coverUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
            {isHost && (
              <button
                data-testid="cover-change-btn"
                onClick={() => coverInputRef.current?.click()}
                className="absolute bottom-3 right-3 kinetic-btn bg-[#141414]/80 backdrop-blur border border-white/20 hover:border-white/60 px-3 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2"
              >
                <ImageIcon weight="bold" size={14} /> {t("profile.change")}
              </button>
            )}
          </div>
        )}
        {isHost && !coverUrl && (
          <button
            data-testid="cover-upload-btn"
            onClick={() => coverInputRef.current?.click()}
            className="mb-6 w-full border border-dashed border-[#262626] hover:border-[#007AFF] hover:bg-[#007AFF]/5 transition-colors py-6 flex items-center justify-center gap-2 text-sm text-zinc-400"
          >
            <ImageIcon weight="duotone" size={20} />
            <span>Add cover photo</span>
          </button>
        )}
        <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="cover-file-input" />
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${meta.color}1F`, border: `1px solid ${meta.color}` }}>
                <meta.Icon weight="duotone" size={22} color={meta.color} />
              </div>
              <div className="label-overline" style={{ color: meta.color }}>{meta.label}</div>
              <span className="font-mono-tag text-xs text-zinc-400">· {a.skill_level}</span>
              {a.is_private && <span className="font-mono-tag text-xs text-zinc-400">· {t("common.private").toUpperCase()}</span>}
              {isPast && <span className="font-mono-tag text-xs px-2 py-0.5 bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/40 uppercase tracking-widest">Completed</span>}
            </div>
            <h1 data-testid="activity-title" className="font-display text-5xl tracking-tight">{a.title}</h1>
            <p className="text-zinc-400 mt-3 max-w-2xl">{a.description || t("activity.no_desc")}</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-zinc-300">
              <CalendarBlank weight="duotone" size={16} />
              <span className="font-mono-tag">{isNaN(startDate) ? a.starts_at : startDate.toLocaleString()}</span>
            </div>
            <div className="mt-2 text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
              <span>{t("activity.host_by")}</span>
              <span className="text-white hover:underline">{a.host_name}</span>
              {a.host_is_pro && <ProBadge size={10} />}
            </div>
          </div>

          <div className="flex flex-col gap-2 justify-end">
            {isPast && isParticipant && (
              <button data-testid="open-rating-btn" onClick={() => setShowRating(true)} className="kinetic-btn bg-[#FFCC00] hover:bg-[#e6b800] text-black px-4 py-3 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm">
                <Star weight="fill" size={16} /> {t("activity.rate_now")}
              </button>
            )}
            {isHost ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Link
                  to={`/activity/${id}/edit`}
                  data-testid="activity-edit-btn"
                  className="kinetic-btn border border-[#262626] hover:border-white/40 text-white px-4 py-3 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm"
                >
                  <PencilSimple weight="bold" size={16} /> {t("activity.edit_action")}
                </Link>
                <button data-testid="activity-delete-btn" onClick={del} className="kinetic-btn border border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 text-[#FF3B30] px-4 py-3 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm">
                  <Trash weight="bold" size={16} /> {t("activity.delete_action")}
                </button>
              </div>
            ) : isParticipant ? (
              <button data-testid="activity-leave-btn" onClick={leave} className="kinetic-btn border border-[#262626] hover:border-white/40 text-white px-4 py-3 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm">
                <SignOut weight="bold" size={16} /> {t("activity.leave_action")}
              </button>
            ) : isWaitlisted ? (
              <button disabled className="border border-[#262626] text-zinc-400 px-4 py-3 font-bold uppercase tracking-widest text-sm">{t("activity.waitlisted_label")}</button>
            ) : (
              <button data-testid="activity-join-btn" onClick={join} className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
                {isFull ? t("activity.join_waitlist") : t("activity.join_action")}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#262626] mb-6 bg-[#141414]">
          {a.sport === "padel" ? (
            <>
              <Stat
                icon={<MapPin size={16} weight="duotone" color="#34C759" />}
                label={t("common.venue") || (a.start_point ? "Venue" : "Venue")}
                value={a.start_point?.address ? a.start_point.address.split(",")[0] : (a.start_point?.label || "—")}
              />
              <Stat icon={<CalendarBlank size={16} weight="duotone" color="#FF3B30" />} label={t("common.skill_level") || "Skill"} value={a.skill_level || "—"} />
              <Stat icon={<Lightning size={16} weight="duotone" color="#FFCC00" />} label={t("common.visibility") || "Visibility"} value={a.is_private ? (t("common.private") || "Private") : (t("common.public") || "Public")} />
              <Stat icon={<Users size={16} weight="duotone" />} label={t("common.spots")} value={`${a.participants.length}/${a.max_participants}`} />
            </>
          ) : (
            <>
              <Stat icon={<MapPin size={16} weight="duotone" color="#007AFF" />} label={t("common.distance")} value={a.distance_km ? `${a.distance_km} ${t("common.km")}` : "—"} />
              <Stat icon={<Clock size={16} weight="duotone" color="#FF3B30" />} label={t("common.duration")} value={a.duration_min ? `${Math.round(a.duration_min)} ${t("common.min")}` : "—"} />
              <Stat icon={<Lightning size={16} weight="duotone" color="#34C759" />} label={t("common.pace")} value={a.pace || "—"} />
              <Stat icon={<Users size={16} weight="duotone" />} label={t("common.spots")} value={`${a.participants.length}/${a.max_participants}`} />
            </>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {a.sport === "padel" ? (
              a.start_point ? (
                <>
                  <RouteMap start={a.start_point} end={null} geometry={null} liveLocations={liveLocations} height={420} />
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
                    <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#34C759] rounded-full" /> {a.start_point.address || a.start_point.label || (t("common.venue") || "Venue")}</div>
                    {liveLocations.length > 0 && (
                      <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" /> {liveLocations.length} {t("activity.location_active")}</div>
                    )}
                  </div>
                </>
              ) : (
                <div className="border border-dashed border-[#262626] bg-[#141414] p-10 text-center text-sm text-zinc-500" data-testid="padel-no-venue">
                  {(a?.description?.length ?? 0) > 0
                    ? (t("activity.padel_no_venue_with_desc") || "No venue selected — see description above for meeting details.")
                    : (t("activity.padel_no_venue") || "No venue selected for this padel session.")}
                </div>
              )
            ) : (
              <>
                <RouteMap start={a.start_point} end={a.end_point} geometry={a.route_geometry} liveLocations={liveLocations} height={420} />
                <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4 text-xs text-zinc-400">
                    {a.start_point && (
                      <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#007AFF] rounded-full" /> {a.start_point.label || t("common.start")}</div>
                    )}
                    {a.end_point && <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF3B30] rounded-full" /> {a.end_point.label || t("common.end")}</div>}
                    {liveLocations.length > 0 && (
                      <div className="flex items-center gap-2"><span className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" /> {liveLocations.length} {t("activity.location_active")}</div>
                    )}
                  </div>
                  {canSeeChat && !isPast && (
                    <LiveLocationSharer activityId={a.activity_id} onUpdate={setLiveLocations} />
                  )}
                </div>
              </>
            )}
          </div>
          <div>
            {canSeeChat ? (
              <ChatPanel activityId={a.activity_id} currentUser={user} />
            ) : (
              <div className="border border-dashed border-[#262626] p-6 h-[420px] flex items-center justify-center text-center">
                <div>
                  <div className="label-overline">{t("activity.chat_locked")}</div>
                  <div className="text-sm text-zinc-400 mt-2">{t("activity.chat_locked_text")}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-display text-3xl tracking-tight">{t("activity.crew")}</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {a.participants.map((uid) => {
              const p = participants.find((x) => x.user_id === uid);
              return <PersonRow key={uid} p={p} role={uid === a.host_id ? t("activity.host") : t("activity.member")} />;
            })}
          </div>
          {a.waitlist.length > 0 && (
            <>
              <h3 className="label-overline mt-8">{t("activity.waitlist")}</h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {a.waitlist.map((uid) => {
                  const p = participants.find((x) => x.user_id === uid);
                  return <PersonRow key={uid} p={p} role={t("activity.waiting")} />;
                })}
              </div>
            </>
          )}
        </section>
      </main>

      {showRating && (
        <RatingModal activity={a} currentUser={user} onClose={() => setShowRating(false)} />
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="p-5 border-r border-[#262626] last:border-r-0">
      <div className="flex items-center gap-2">{icon}<span className="label-overline">{label}</span></div>
      <div className="font-display text-2xl mt-2 leading-none">{value}</div>
    </div>
  );
}

function PersonRow({ p, role }) {
  if (!p) return <div className="tf-card p-3 text-xs text-zinc-500">…</div>;
  return (
    <div className="tf-card p-3 flex items-center gap-3">
      {p.picture ? (
        <img src={p.picture.startsWith("http") ? p.picture : `${process.env.REACT_APP_BACKEND_URL}${p.picture}`} alt="" className="w-9 h-9 object-cover" />
      ) : (
        <div className="w-9 h-9 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center text-xs">{(p.name || "?")[0]}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{p.name}</div>
        <div className="label-overline">{role}{p.fitness_level ? ` · ${p.fitness_level}` : ""}</div>
      </div>
    </div>
  );
}
